import { useState, useEffect, Fragment, useCallback, memo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  Grid as MuiGrid,
  Accordion as MuiAccordion,
  Typography as MuiTypography,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  ExpandMore as MuiExpandMore,
  Info as InfoIcon,
} from "@mui/icons-material";
import { setFieldsInText, shouldRenderElement } from "app/shared/utils";
import isJSON from "is-json";
import { FormElement } from "app/forms/FormElement";
import { useForms } from "app/forms/formsService";
import { useTranslation } from "react-i18next";

const accordionSummary = {
  backgroundColor: "#F7F7F7",
  border: 1,
  borderColor: "#E0E0E0",
  flexDirection: "row-reverse",
  "&.MuiButtonBase-root.MuiAccordionSummary-root": {
    paddingLeft: "10px",
    "& .MuiSvgIcon-root": {
      color: "#717171",
      rotate: "180deg",
    },
  },
};

let timeoutId;
export const Section = memo((props) => {
  let {
    name,
    label,
    formValues,
    actionid,
    formspec,
    element,
    formFlatMap,
    globalValueObj,
    formik,
    render,
    fieldAttr,
    allFields,
    setScrollDialog,
    setRender,
    setInfoDialog,
    pageType,
    duplicateIds,
    yup,
    type,
    summaryFields,
    activeSection,
    setActiveSection,
    sectionsReferenceList,
    setNavigableSections,
    children,
  } = props;
  const { t } = useTranslation();
  const { listGroupedSections } = useForms();
  const { hash = "" } = useLocation();
  const navigate = useNavigate();
  const regex = /#field-/g; // Matches all occurrences of "#field-"
  const fieldId = hash.replace(regex, ""); // Replace "#field-" with an empty string
  const isSummaryForm = type === "summary";
  const trimmedLabel = label?.trim();

  /**
   * @constant {Boolean} isExpandable - isExpandable flag helps us to determine if the section is an Accordion.
   * @constant {Boolean} isOpen - isOpen flag helps us to determine if the Accordion is open or close.
   * @constant {Array} field_group - field_group is the list of all the fields for Accordion Details.
   */
  const {
    isExpandable = false,
    isOpen = false,
    isNavigable = false,
  } = isJSON(fieldAttr) ? JSON.parse(fieldAttr) : {};

  const accordionDetails = {
    padding: isSummaryForm ? 0 : "16px",
    borderBottom: 1,
    borderRight: 1,
    borderLeft: 1,
    borderColor: "#E0E0E0",
    display: "grid",
    gap: 2,
  };

  const [text, setText] = useState("");
  const [fieldRenderedExp, setFieldRenderedExp] = useState("");
  const [shouldRender, setShouldRender] = useState(false);
  const [openAccordionPanel, setOpenAccordionPanel] = useState(isOpen);

  useEffect(() => {
    setOpenAccordionPanel(isOpen);
  }, [isOpen]);

  // This useEffect will help us open the accordion and highlight the fields with errors when we navigate from the error list.
  useEffect(() => {
    if (listGroupedSections[element.field_id]?.includes(fieldId)) {
      setOpenAccordionPanel(true);

      clearTimeout(timeoutId); // Cancel current debounce timer
      timeoutId = setTimeout(() => {
        const field = document.querySelector(`#${fieldId}`);
        field?.focus();
        field?.scrollIntoView({ behavior: "smooth", block: "center" });

        /**
         * Removing the hash value from the URL so this useEffect can be re-triggered
         * if someone clicked twice to navigate to the field error from the error list
         */
        navigate(null, { replace: true });
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId]);

  // We are extracting the objects of the fields that needs to be grouped
  const fields =
    listGroupedSections[element.field_id]?.map((item) =>
      getFieldObject(item, allFields),
    ) ?? [];

  const fieldsFlatList = fields.flat(Infinity);

  // Focus on the section when activeSection is equal to the label and opens the accordion for that section
  useEffect(() => {
    if (activeSection === trimmedLabel) {
      // Get the element by ID and scroll to it if the element exists
      const element = document.getElementById(trimmedLabel);
      if (element) {
        setOpenAccordionPanel(true);
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [activeSection, trimmedLabel]);

  useEffect(() => {
    // Field Rendered evaluation - Should render this component or not, will depend on field rendered evaluation
    setFieldRenderedExp(
      shouldRenderElement(
        element.field_rendered,
        element.field_value_type,
        {
          ...formFlatMap,
          ...globalValueObj,
          ...formik.values,
        },
        element.field_editable,
      ),
    );
  }, [
    element.field_id,
    element.field_rendered,
    element.field_editable,
    globalValueObj,
    formFlatMap,
    formik,
    element.field_value_type,
    render,
  ]);

  useEffect(() => {
    try {
      // eslint-disable-next-line no-eval
      const evaledExp = eval(fieldRenderedExp); // SYNC and ASYNC

      setShouldRender(evaledExp);
    } catch (err) {
      console.log(name, err);
    }
  }, [fieldRenderedExp, name]);

  useEffect(() => {
    setFieldsInText(
      label,
      formValues,
      {
        actionId: actionid,
        formSpec: formspec,
      },
      setText,
    );
  }, [label, formValues, actionid, formspec, formik]);

  // Function to sort the sections based on their order in the sectionsReferenceList
  const sortNavigableSections = useCallback(
    (sectionsList) => {
      if (!sectionsReferenceList || sectionsReferenceList.length === 0)
        return sectionsList;

      return sectionsList
        .filter((label) => sectionsReferenceList.has(label))
        .sort(
          (labelA, labelB) =>
            sectionsReferenceList.get(labelA) -
            sectionsReferenceList.get(labelB),
        );
    },
    [sectionsReferenceList],
  );

  // Adds or removes a section from navigableSections based on shouldRender and isNavigable
  const updateNavigableSections = useCallback(() => {
    setNavigableSections((prev) => {
      let newSections = [...prev];
      const exists = newSections.includes(trimmedLabel);

      if (isNavigable && shouldRender && !exists) {
        newSections.push(trimmedLabel);
      } else if ((!isNavigable || !shouldRender) && exists) {
        newSections = newSections.filter((section) => section !== trimmedLabel);
      }

      return sortNavigableSections(newSections);
    });
  }, [
    isNavigable,
    setNavigableSections,
    shouldRender,
    sortNavigableSections,
    trimmedLabel,
  ]);

  // Accordion click handler: Open or close the accordion panel
  const handleAccordionChange = () => {
    setOpenAccordionPanel(!openAccordionPanel);
    // Reset the active section to null when the accordion is closed
    if (!!openAccordionPanel && typeof setActiveSection === "function") {
      setActiveSection(null);
    }
  };

  useEffect(() => {
    updateNavigableSections();
  }, [updateNavigableSections]);

  if (!shouldRender) return null;

  return (
    <MuiGrid container size={12} id={trimmedLabel}>
      <MuiAccordion
        square
        disableGutters
        expanded={isExpandable ? openAccordionPanel : true}
        onChange={isExpandable ? handleAccordionChange : null}
        sx={{ boxShadow: "none", width: "100%" }}
      >
        <MuiAccordionSummary
          expandIcon={isExpandable ? <MuiExpandMore /> : null}
          sx={
            isExpandable
              ? accordionSummary
              : {
                  ...accordionSummary,
                  "&.MuiButtonBase-root.MuiAccordionSummary-root": {
                    paddingLeft: "16px",
                  },
                }
          }
        >
          <MuiTypography sx={{ fontSize: "20px" }}>
            {text}
            {element && Boolean(element.field_info?.trim()) && (
              <MuiTooltip title={t("globals.fieldInfo.tooltip")}>
                <InfoIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoDialog({
                      show: true,
                      fieldLabel: label,
                      fieldContent: element.field_info,
                    });
                  }}
                  sx={{
                    cursor: "pointer",
                    fontSize: "18.75px",
                    marginLeft: "7em",
                  }}
                />
              </MuiTooltip>
            )}
          </MuiTypography>
        </MuiAccordionSummary>
        <MuiAccordionDetails sx={accordionDetails}>
          {isSummaryForm ? (
            <>{children}</>
          ) : (
            fieldsFlatList.map((field, index) => (
              <Fragment key={index}>
                <FormElement
                  element={field}
                  formik={formik}
                  actionId={actionid}
                  formFields={fieldsFlatList}
                  formFlatMap={formFlatMap}
                  allFields={allFields}
                  renderedExp={field.field_rendered}
                  globalValueObj={globalValueObj}
                  setScrollDialog={setScrollDialog}
                  render={render}
                  setRender={setRender}
                  setInfoDialog={setInfoDialog}
                  pageType={pageType}
                  duplicateIds={duplicateIds}
                  yup={yup}
                  summaryFields={summaryFields}
                  t={t}
                />
              </Fragment>
            ))
          )}
        </MuiAccordionDetails>
      </MuiAccordion>
    </MuiGrid>
  );
});

const getFieldObject = (key, fields) => {
  const listOfFields = fields.filter((item) => item.field_id === key);
  return listOfFields;
};
